import type {
  InviteResult,
  Member,
  MemberListFilters,
  MemberTableGroup,
  Organization,
  OrgChip,
  OrgRole,
  OrgTabId,
} from './types'
import { getBranchColor } from '../../../BranchSwitcher/colors'
import { abbreviateBranchName } from '../../../BranchSwitcher/BranchAvatar'
import { HARD_MONEY_ROLE_OPTIONS, ROLE_OPTIONS } from './constants'

export function orgTabLabel(org: Organization): string {
  if (org.short === 'PA') return 'Palo Alto'
  if (org.short === 'SJ') return 'San Jose'
  return org.name.replace('GMCC — ', '')
}

const organizationNameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

export function compareOrganizationNames(a: string, b: string): number {
  return organizationNameCollator.compare(a, b)
}

export function sortOrganizationsByName<T extends { name: string }>(organizations: T[]): T[] {
  return [...organizations].sort((a, b) => compareOrganizationNames(a.name, b.name))
}

export function resolveManagedOrgTab(prev: OrgTabId, organizations: Organization[]): OrgTabId {
  const manageOrgs = organizations.filter((org) => org.canManageUsers)
  if (manageOrgs.length === 1) return manageOrgs[0].id
  if (prev === 'all' || manageOrgs.some((org) => org.id === prev)) return prev
  return 'all'
}

export function sortOrgChips(chips: OrgChip[]): OrgChip[] {
  return [...chips].sort((a, b) => compareOrganizationNames(a.name || a.label, b.name || b.label))
}

export function normalizeOrganizationPlan(plan?: string | null): string {
  const value = (plan || '').trim().toLowerCase()
  return value || 'starter'
}

export function organizationPlan(org: Organization | undefined): string {
  return normalizeOrganizationPlan(org?.plan)
}

export function resolveOrganizationMemberCount(org: Organization | undefined, fallback = 0): number {
  return typeof org?.memberCount === 'number' ? org.memberCount : fallback
}

export function canAddUsersToOrganization(org: Organization | undefined): boolean {
  const plan = organizationPlan(org)
  return plan === 'business' || plan === 'enterprise'
}

function orgRoleNames(org: Organization | undefined): string[] {
  if (!org) return []
  return [org.myRole, ...(org.roles || [])]
}

export function managerHasSuperAdmin(org: Organization | undefined): boolean {
  return orgRoleNames(org).some((role) => role === 'Super Admin' || role === 'SuperAdmin')
}

export function managerCanAssignOwner(org: Organization | undefined): boolean {
  if (managerHasSuperAdmin(org)) return true
  return orgRoleNames(org).some((role) => role === 'Owner')
}

export function hasSuperAdminRole(roles: OrgRole[] | undefined): boolean {
  return (roles || []).some((role) => role === 'Super Admin')
}

const PROTECTED_ROLES: OrgRole[] = ['LO Manager', 'Owner', 'Super Admin']

export function protectedRolesFromList(roles: OrgRole[] | undefined): OrgRole[] {
  return (roles || []).filter((role) => PROTECTED_ROLES.includes(role))
}

export function existingProtectedRoles(member: { roleLists?: Record<string, OrgRole[]>; roles?: Record<string, OrgRole> } | null, orgId: string): OrgRole[] {
  if (!member) return []
  const roles = member.roleLists?.[orgId] || (member.roles?.[orgId] ? [member.roles[orgId]] : [])
  return protectedRolesFromList(roles)
}

export function organizationHasPaidSeatLimit(org: Organization | undefined): boolean {
  return organizationPlan(org) === 'business'
}

export interface OrganizationSeatUsage {
  paidSeats: number
  paidUsers: number
  remaining: number
  full: boolean
}

export function organizationSeatUsage(org: Organization | undefined): OrganizationSeatUsage | null {
  if (!organizationHasPaidSeatLimit(org)) return null
  if (typeof org?.paidSeats !== 'number' || typeof org?.paidUsers !== 'number') return null
  const remaining = Math.max(0, org.paidSeats - org.paidUsers)
  return {
    paidSeats: org.paidSeats,
    paidUsers: org.paidUsers,
    remaining,
    full: org.paidSeats > 0 && remaining === 0,
  }
}

function paidUsersCountLabel(paidUsers: number): string {
  return `${paidUsers} paid user${paidUsers === 1 ? '' : 's'}`
}

export function organizationSeatUsageLabel(org: Organization | undefined): string | null {
  const usage = organizationSeatUsage(org)
  if (!usage) return null
  return paidUsersCountLabel(usage.paidUsers)
}

export function organizationPaidUsersLabel(org: Organization | undefined): string | null {
  const plan = organizationPlan(org)
  if (plan !== 'business' && plan !== 'enterprise') return null
  if (typeof org?.paidUsers !== 'number') return null
  return paidUsersCountLabel(org.paidUsers)
}

export function accountTierLabel(tier?: string): 'Paid' | 'Free' {
  return (tier || '').toLowerCase() === 'paid' ? 'Paid' : 'Free'
}

export function formatSeatOutcomes(
  results: Array<{ customerId: string; companyName?: string; accountTier?: string }>,
  organizations: Organization[],
): string {
  return results
    .map((result) => {
      const org = organizations.find((item) => item.customerId === result.customerId || item.id === result.customerId)
      const name = org?.short || result.companyName || result.customerId
      if (!result.accountTier) return ''
      return `${name} ${accountTierLabel(result.accountTier)}`
    })
    .filter(Boolean)
    .join(', ')
}

export function inviteResultOrganizationName(
  result: Pick<InviteResult, 'customerId' | 'companyName'>,
  organizations: Organization[],
): string {
  const org = organizations.find((item) => item.customerId === result.customerId || item.id === result.customerId)
  return org?.name || result.companyName || result.customerId
}

export function formatInviteFailureMessage(
  results: InviteResult[],
  organizations: Organization[],
): string {
  return results
    .map((result) => {
      const name = inviteResultOrganizationName(result, organizations)
      const reason = result.exists
        ? (result.text || 'this user already belongs to the organization')
        : (result.text || 'Invitation failed')
      return `${name}: ${reason}`
    })
    .join('\n')
}

export function formatInviteSuccessMessage(
  results: InviteResult[],
  organizations: Organization[],
): string {
  return results
    .map((result) => `${inviteResultOrganizationName(result, organizations)}: added successfully`)
    .join('\n')
}

export function isCurrentSessionOrganization(
  org: Organization,
  currentCustomerId?: string | null,
): boolean {
  if (!currentCustomerId) return false
  return org.customerId === currentCustomerId || org.id === currentCustomerId
}

export function makeOrgShort(name: string): string {
  return abbreviateBranchName(name)
}

export function filterMembers(members: Member[], search: string): Member[] {
  const q = search.trim().toLowerCase()
  if (!q) return members
  return members.filter(
    (m) =>
      m.email.toLowerCase().includes(q) ||
      m.name.toLowerCase().includes(q) ||
      m.nmls.toLowerCase().includes(q),
  )
}

export function buildMemberGroups(
  adminOrgs: Organization[],
  allOrgs: Organization[],
  members: Member[],
  orgTab: OrgTabId,
): MemberTableGroup[] {
  const groups = orgTab === 'all' ? adminOrgs : adminOrgs.filter((org) => org.id === orgTab)
  return groups.map((org) => ({
    org,
    rows: members
      .filter((member) => member.roles[org.id])
      .map((member) => ({
        member,
        roleInOrg: member.roles[org.id],
        roleChips: buildRoleChips(org.id, member.roleLists?.[org.id] || [member.roles[org.id]]),
        orgChips: Object.keys(member.roles).map((orgId) => {
          const chipOrg = allOrgs.find((o) => o.id === orgId)
          return {
            id: orgId,
            label: chipOrg?.short || orgId,
            name: chipOrg?.name || orgId,
            color: chipOrg?.color || '#98A2B3',
          }
        }),
      })),
  }))
}

export function pickOrgColor(index: number): string {
  return getBranchColor(index)
}

export function orgChipFromOrganization(org: Organization): OrgChip {
  return {
    id: org.id,
    label: org.short,
    name: org.name,
    color: org.color,
  }
}

export function orgChipsForMember(
  member: Member,
  organizations: Organization[],
  fallback: Organization,
): OrgChip[] {
  const byCustomerId = new Map<string, Organization>()
  organizations.forEach((org) => {
    byCustomerId.set(org.customerId, org)
    byCustomerId.set(org.id, org)
  })
  const ids = member.organizationIds?.length ? member.organizationIds : [fallback.customerId]
  const chips: OrgChip[] = []
  ids.forEach((id) => {
    const org = byCustomerId.get(id)
    if (!org || chips.some((chip) => chip.id === org.id)) return
    chips.push(orgChipFromOrganization(org))
  })
  return sortOrgChips(chips.length > 0 ? chips : [orgChipFromOrganization(fallback)])
}

const ROLE_COLORS: Record<string, string> = {
  'Loan Officer': '#2B5CE6',
  Processor: '#0E9384',
  'LO Manager': '#7A5AF8',
  Owner: '#DC6803',
  'Super Admin': '#D92D20',
  Underwriter: '#079455',
  Closer: '#DD2590',
  Admin: '#4E5BA6',
}

export function pickRoleColor(role: string | undefined): string {
  return (role && ROLE_COLORS[role]) || '#667085'
}

export function buildRoleChips(orgId: string, roles: Array<OrgRole | undefined>): OrgChip[] {
  return roles.filter((role): role is OrgRole => !!role).map((role) => ({
    id: `${orgId}-${role}`,
    label: role,
    name: role,
    color: pickRoleColor(role),
  }))
}

/** Full role filter set for User Management (Owner / Super Admin only). */
export function roleFilterOptions(organizations: Organization[]): OrgRole[] {
  const options: OrgRole[] = ['Loan Officer', 'Processor']
  if (organizations.some((org) => org.isHardMoneyLoanCustomer)) {
    options.push(...HARD_MONEY_ROLE_OPTIONS)
  }
  options.push('LO Manager', 'Owner', 'Super Admin')
  return options
}

export function memberQueryKey(search: string, filters: MemberListFilters): string {
  return JSON.stringify({
    search: search.trim(),
    roles: [...filters.roles].sort(),
    organizationIds: [...filters.organizationIds].sort(),
    status: filters.status || '',
    accountTier: filters.accountTier || '',
  })
}

export function rolesFromSelections(
  selections: Record<string, { checked: boolean; roles: OrgRole[] }>,
): Record<string, OrgRole[]> {
  const roles: Record<string, OrgRole[]> = {}
  Object.entries(selections).forEach(([orgId, selection]) => {
    if (selection.checked) roles[orgId] = selection.roles
  })
  return roles
}

export function buildOrgSelections(
  orgs: Organization[],
  memberRoles: Record<string, OrgRole> | undefined,
  defaultCheckedOrgId?: string,
  memberRoleLists?: Record<string, OrgRole[]>,
): Record<string, { checked: boolean; roles: OrgRole[] }> {
  const selections: Record<string, { checked: boolean; roles: OrgRole[] }> = {}
  orgs.forEach((org) => {
    const memberRole = memberRoles?.[org.id]
    const orgRoles = memberRoleLists?.[org.id] || (memberRole ? [memberRole] : [])
    const options: OrgRole[] = org.isHardMoneyLoanCustomer
      ? [...ROLE_OPTIONS, ...HARD_MONEY_ROLE_OPTIONS]
      : [...ROLE_OPTIONS]
    if ((managerHasSuperAdmin(org) || orgRoles.includes('Super Admin')) && !options.includes('Super Admin')) {
      options.push('Super Admin')
    }
    const assignableRoles = orgRoles.filter((role) => options.includes(role as typeof options[number]))
    selections[org.id] = {
      checked: memberRoles ? !!memberRoles[org.id] : org.id === defaultCheckedOrgId,
      roles: assignableRoles.length > 0 ? assignableRoles : ['Loan Officer'],
    }
  })
  return selections
}
